import { memo, useEffect, useMemo, useState, useCallback } from "react";
import { useSelector, shallowEqual } from "react-redux";
import ApiCustom from "../../utils/ApiCustom";
import { toast } from "react-toastify";
import ConfirmModal from "../ConfirmModal.js";

/** Helpers: đọc localStorage an toàn & ép kiểu số */
const getLS = (key, fallback = "") => {
  try {
    const v = localStorage.getItem(key);
    return v ?? fallback;
  } catch {
    return fallback;
  }
};
const parseNumber = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : "";
};

const getDateOnlyTime = (value) => {
  const date = new Date(value);
  date.setHours(0, 0, 0, 0);
  return date.getTime();
};

/** Khởi tạo "lười" để không đọc localStorage trên mỗi re-render */
const initFormData = (noti) => ({
  fullName: getLS("full_name", ""),
  email: getLS("userEmail", ""),
  phoneNumber: getLS("userPhone", ""),
  companyId: parseNumber(getLS("companyId", "")),
  postId: noti?.id ?? "",
});

const BusinessForm = ({ noti, isRegistedEvent, sendIsRegistedStatus }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState(() => initFormData(noti));
  const [errors, setErrors] = useState({});
  const [isOpenModal, setIsOpenModal] = useState(false);

  /** Chỉ lấy đúng phần cần từ redux để giảm render */
  const partners = useSelector(
    (state) => state.partners?.partners,
    shallowEqual,
  );
  const postsState = useSelector((state) => state.posts?.posts, shallowEqual);

  /** Lọc posts type=notification chỉ khi nguồn đổi */
  const posts = useMemo(() => {
    const list = postsState || [];
    return list.filter((p) => p?.type === "notification");
  }, [postsState]);

  /** Cập nhật postId theo noti.id (functional update tránh stale) */
  useEffect(() => {
    if (!noti?.id) return;
    setFormData((prev) =>
      prev.postId === noti.id ? prev : { ...prev, postId: noti.id },
    );
  }, [noti?.id]);

  const handleCloseModal = useCallback(() => setIsOpenModal(false), []);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === "companyId" ? parseNumber(value) : value,
    }));
    setErrors((prev) => ({ ...prev, [name]: undefined }));
  }, []);

  /** Tính trạng thái thời gian (nhãn + khóa nút) */
  const timing = useMemo(() => {
    const today = getDateOnlyTime(new Date());

    const rs = noti?.regisStartTime
      ? getDateOnlyTime(noti.regisStartTime)
      : NaN;
    const re = noti?.regisEndTime ? getDateOnlyTime(noti.regisEndTime) : NaN;

    if (Number.isFinite(rs) && today < rs) {
      return {
        code: "BEFORE_REG",
        label: "Chưa đến thời gian đăng ký",
        lock: true,
      };
    }

    if (Number.isFinite(re) && today > re) {
      return { code: "REG_CLOSED", label: "Hết hạn đăng ký", lock: true };
    }

    return { code: "OPEN", label: "Đăng ký tham gia", lock: false };
  }, [noti?.regisStartTime, noti?.regisEndTime]);

  /** Submit */
  const handleSubmit = useCallback(async () => {
    setLoading(true);
    try {
      const payload = {
        companyId: formData.companyId,
        postId: formData.postId,
        fullName: formData.fullName,
        email: formData.email,
        phoneNumber: formData.phoneNumber,
      };

      const res = await ApiCustom({
        method: "post",
        url: "eventRegistration-create",
        data: payload,
        headers: { "Content-Type": "application/json" },
      });

      toast(res?.data?.message || "Đăng ký thành công", { type: "success" });

      // Reset gọn
      setFormData({
        fullName: "",
        email: "",
        phoneNumber: "",
        companyId: "",
        postId: "",
      });

      sendIsRegistedStatus?.(true);
      setErrors({});
    } catch (error) {
      const status = error?.response?.status;
      if (status === 422) {
        const msgMap = error?.response?.data?.messages;
        const firstMsg = msgMap
          ? Object.values(msgMap)[0]
          : "Dữ liệu không hợp lệ";
        toast.error(firstMsg);
      } else {
        toast.error("Đã xảy ra lỗi");
      }
    } finally {
      handleCloseModal();
      setLoading(false);
    }
  }, [formData, handleCloseModal, sendIsRegistedStatus]);

  return (
    <>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setIsOpenModal(true);
        }}
        className="space-y-4"
      >
        {/* Các input đang hidden — giữ nguyên để không đổi logic, nhưng vẫn controlled */}
        <div className="form-control hidden">
          <label className="text-sm mb-1">
            Họ tên người tham gia<i className="text-red-600"> *</i>
          </label>
          <input
            type="text"
            name="fullName"
            value={formData.fullName}
            onChange={handleChange}
            className="input input-bordered w-full"
            placeholder="Họ tên người tham gia"
            autoComplete="name"
          />
          <p className="text-red-500 text-xs">{errors.fullName}</p>
        </div>

        <div className="form-control hidden">
          <label className="text-sm mb-1">Email</label>
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            className="input input-bordered w-full"
            placeholder="Email"
            autoComplete="email"
          />
          <p className="text-red-500 text-xs">{errors.email}</p>
        </div>

        <div className="form-control hidden">
          <label className="text-sm mb-1">Số điện thoại</label>
          <input
            type="text"
            name="phoneNumber"
            value={formData.phoneNumber}
            onChange={handleChange}
            className="input input-bordered w-full"
            placeholder="Số điện thoại"
            autoComplete="tel"
            inputMode="tel"
          />
          <p className="text-red-500 text-xs">{errors.phoneNumber}</p>
        </div>

        <div className="form-control hidden">
          <label className="text-sm mb-1">
            Đơn vị<i className="text-red-600"> *</i>
          </label>
          <select
            name="companyId"
            value={formData.companyId}
            onChange={handleChange}
            className="select select-bordered w-full"
          >
            <option value="">Chọn đơn vị</option>
            {partners?.map((company) => (
              <option key={company.id} value={company.id}>
                {company.companyName}
              </option>
            ))}
          </select>
          <p className="text-red-500 text-xs">{errors.companyId}</p>
        </div>

        <div className="form-control hidden">
          <label className="text-sm mb-1">
            Chọn hoạt động tham gia<i className="text-red-600"> *</i>
          </label>
          <select
            name="postId"
            value={formData.postId}
            onChange={handleChange}
            className="select select-bordered w-full"
          >
            <option value="">Chọn hoạt động</option>
            {posts.map((post) => (
              <option key={post.id} value={post.id}>
                {post.title}
              </option>
            ))}
          </select>
          <p className="text-red-500 text-xs">{errors.postId}</p>
        </div>

        <button
          type="submit"
          className="btn btn-primary w-full"
          disabled={timing.lock || loading || isRegistedEvent}
          aria-busy={loading ? "true" : "false"}
        >
          {timing.lock ? (
            timing.label
          ) : loading ? (
            <span className="loading loading-spinner" />
          ) : !isRegistedEvent ? (
            timing.label /* "Đăng ký tham gia" */
          ) : (
            "Đã đăng ký tham gia"
          )}
        </button>
      </form>

      <ConfirmModal
        isOpen={isOpenModal}
        onClose={handleCloseModal}
        onSubmit={handleSubmit}
        message={"Bạn có chắc chắn muốn đăng ký tham gia không?"}
      />
    </>
  );
};

export default memo(BusinessForm);